import { UndoOutlined } from "@ant-design/icons";
import { Button, notification, Progress } from "antd";
import { NotificationPlacement } from "antd/es/notification/interface";
import React from "react";
import { ReactComponent as NotiCloseIcon } from "../../../common/images/svg/NotiClose.svg";
import { ReactComponent as NotiErrorIcon } from "../../../common/images/svg/NotiError.svg";
import { ReactComponent as NotiInfoIcon } from "../../../common/images/svg/NotiInfo.svg";
import { ReactComponent as NotiSuccessIcon } from "../../../common/images/svg/NotiSuccess.svg";
import { ReactComponent as NotiWarningIcon } from "../../../common/images/svg/NotiWarning.svg";
import "../style.css";
import { NotificationProvider } from "./INotificationContext";

type TypeUndoableNotificationProps = {
  notificationKey?: string;
  message: string;
  cancelMutation?: () => void;
  undoableTimeout?: number;
};

const NOTIFICATION_TIME_IN_MS = 10000006000;

export const handlerNotification = (
  type: "error" | "success" | "warning" | "info",
  placement: NotificationPlacement,
  message: string,
  description?: string,
  duration?: number,
  icon?: React.ReactNode,
  closeIcon?: React.ReactNode,
  onClick?: () => void
) => {
  const hasOnClick = typeof onClick === "function";

  notification[type]({
    message,
    description,
    placement,
    icon,
    duration,
    closeIcon,
    onClick: () => {
      if (hasOnClick) {
        onClick?.();
        notification.destroy();
      }
    },
    className: `${hasOnClick ? "notification-realtime" : undefined} `,
  });
  setTimeout(() => {
    notification.destroy();
  }, NOTIFICATION_TIME_IN_MS);
};

export const SmartUndoableNotification: React.FC<
  TypeUndoableNotificationProps
> = ({ notificationKey, message, cancelMutation, undoableTimeout }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginTop: "-7px",
    }}
  >
    <Progress
      type="circle"
      percent={(undoableTimeout ?? 0) * 20}
      format={(time) => time && time / 20}
      width={50}
      strokeColor="#1890ff"
      status="normal"
    />
    <span style={{ marginLeft: 8, width: "100%" }}>{message}</span>
    <Button
      style={{ flexShrink: 0 }}
      onClick={() => {
        cancelMutation?.();
        notification.destroy(notificationKey ?? "");
      }}
      disabled={undoableTimeout === 0}
      icon={<UndoOutlined />}
    ></Button>
  </div>
);

export const smartNotificationProvider: NotificationProvider = {
  open: ({
    key,
    message,
    description,
    type,
    cancelMutation,
    undoableTimeout,
    icon,
    closeIcon,
    onClick,
  }) => {
    switch (type) {
      case "error": {
        handlerNotification(
          type,
          "topRight",
          message,
          description,
          undoableTimeout,
          (icon = <NotiErrorIcon />),
          (closeIcon = <NotiCloseIcon />),
          onClick
        );
        break;
      }
      case "warning": {
        handlerNotification(
          type,
          "topRight",
          message,
          description,
          undoableTimeout,
          (icon = <NotiWarningIcon />),
          (closeIcon = <NotiCloseIcon />),
          onClick
        );
        break;
      }
      case "success": {
        handlerNotification(
          type,
          "topRight",
          message,
          description,
          undoableTimeout,
          (icon = <NotiSuccessIcon />),
          (closeIcon = <NotiCloseIcon />),
          onClick
        );
        break;
      }
      case "info": {
        handlerNotification(
          type,
          "topRight",
          message,
          description,
          undoableTimeout,
          (icon = <></>),
          (closeIcon = <NotiCloseIcon />),
          onClick
        );
        break;
      }
      default: {
        notification.open({
          key,
          description: (
            <SmartUndoableNotification
              notificationKey={key}
              message={message}
              cancelMutation={cancelMutation}
              undoableTimeout={undoableTimeout}
            />
          ),
          message: null,
          duration: 0,
          closeIcon: <></>,
        });
        break;
      }
    }
  },
  close: (key) => notification.destroy(key),
};
